"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import ReactFlagsSelect from "react-flags-select";

import { useLocale } from "@/app/context/use-locale";

const languageList = ["PL", "UA", "GB", "DE", "FR", "ES", "CN", "RU"];

const localeToCountry: Record<string, string> = {
  en: "GB",
  pl: "PL",
  zh: "CN",
  th: "TH",
  ru: "RU",
  de: "DE",
  fr: "FR",
  es: "ES",
  ua: "UA",
};

const countryToLocale: Record<string, string> = Object.entries(localeToCountry).reduce(
  (acc, [loc, country]) => ({ ...acc, [country]: loc }),
  { PL: "pl" },
);

export default function LanguageChooserComponent() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const defaultFlag = useMemo(() => localeToCountry[locale] ?? "PL", [locale]);
  const [selected, setSelected] = useState(defaultFlag);

  useEffect(() => {
    setSelected(localeToCountry[locale] ?? "PL");
  }, [locale]);

  useEffect(() => {
    const nextLocale = countryToLocale[selected] ?? "pl";
    if (nextLocale === locale) return;

    const currentPath = pathname ?? "/";
    const segments = currentPath.split("/").filter(Boolean);
    if (segments.length === 0) {
      router.push(`/${nextLocale}`);
      return;
    }

    segments[0] = nextLocale;
    const newPath = `/${segments.join("/")}`;
    const query = searchParams?.toString() ?? "";

    router.push(query ? `${newPath}?${query}` : newPath);
  }, [selected, locale, pathname, router, searchParams]);

  return (
    <ReactFlagsSelect
      selectedSize={11}
      optionsSize={11}
      fullWidth={false}
      className="text-black"
      selectButtonClassName="text-black"
      countries={languageList}
      customLabels={{
        PL: "PL",
        UA: "UA",
        GB: "EN",
        FR: "FR",
        DE: "DE",
        ES: "ES",
        CN: "CN",
        RU: "RU",
      }}
      selected={selected}
      onSelect={(countryCode: string) => setSelected(countryCode)}
      showSelectedLabel
      showOptionLabel
    />
  );
}
